import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomInput from "../../components/common/CustomInput";
import CustomTextArea from "../../components/common/CustomTextArea";
import SelectField from "../../components/common/CustomSelectProduct";
import CustomImageUploader from "../../components/common/CustomImageUploader";
import CustomTagSelector from "../../components/common/CustomTagSelector";
import CustomTabs from "../../components/common/CustomTabs";
import ColorSizeManager from "../../components/common/CustomColorSize";
import axios from "axios";
import config from "./../../config";

const AddProductModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    baseprice: "",
    imageUrls: [],
    tags: [],
    sku: "",
    rating: 0,
    shortDescription: "",
    description: "",
    isNew: true,
    variations: [],
  });

  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/Categories`);
        if (response.status === 200) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh mục:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      const categoryId = parseInt(formData.category, 10);
      const basePrice = parseFloat(formData.baseprice);
      const updatedVariations = formData.variations.map((variation) => ({
        ...variation,
        sizes: variation.sizes.map((size) => ({
          ...size,
          price: basePrice, // GÁN GIÁ TỰ ĐỘNG
        })),
      }));
      if (!formData.name.trim() || !formData.sku.trim()) {
        alert("❌ Tên và mã SKU là bắt buộc.");
        setLoading(false);
        return;
      }
      if (isNaN(categoryId) || categoryId <= 0) {
        alert("❌ Vui lòng chọn phân loại hợp lệ.");
        setLoading(false);
        return;
      }
      if (isNaN(basePrice) || basePrice <= 0) {
        alert("❌ Giá nhập phải lớn hơn 0.");
        setLoading(false);
        return;
      }
      const imageCount = formData.imageUrls.filter(img => img.file).length;
      if (imageCount === 0) {
        alert("❌ Vui lòng chọn ít nhất 1 hình ảnh.");
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("categoryId", categoryId.toString());
      formDataToSend.append("SKU", formData.sku);
      formDataToSend.append("BasePrice", basePrice.toString());
      formDataToSend.append("IsNew", formData.isNew ? "true" : "false");
      formDataToSend.append("ShortDescription", formData.shortDescription);
      formDataToSend.append("Description", formData.description);

      updatedVariations.forEach((variation, i) => {
        formDataToSend.append(`Variations[${i}].Color`, variation.color);
        variation.sizes.forEach((size, j) => {
          formDataToSend.append(`Variations[${i}].Sizes[${j}].Size`, size.size);
          formDataToSend.append(`Variations[${i}].Sizes[${j}].Stock`, parseInt(size.stock || 0));
          formDataToSend.append(`Variations[${i}].Sizes[${j}].Price`, basePrice); // dùng basePrice
          formDataToSend.append(`Variations[${i}].Sizes[${j}].Status`, size.status || "active");
        });
      });
      

      formData.tags.forEach((tag, index) => {
        formDataToSend.append(`Tags[${index}]`, tag);
      });

      formData.imageUrls.forEach((image) => {
        if (image.file) {
          formDataToSend.append("imageFiles", image.file);
        }
      });

      const response = await axios.post(
        `${config.apiBaseUrl}/Products/add`,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200 && response.data.productId) {
        alert(`✅ Sản phẩm đã được thêm thành công! (ID: ${response.data.productId})`);
        onSave?.();
        onClose();
      } else {
        alert("❌ Có lỗi xảy ra khi thêm sản phẩm.");
      }
    } catch (error) {
      console.error("❌ Lỗi khi gửi yêu cầu:", error);
      if (error.response) {
        alert(`❌ Lỗi API: ${error.response.status} - ${error.response.data?.Message || "Không xác định"}`);
      } else {
        alert("❌ Không thể kết nối đến server!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (newImages) => {
    setFormData((prev) => ({ ...prev, imageUrls: newImages }));
  };

  // Responsive: Rút gọn nhãn tab trên mobile
  const tabs = isMobile 
    ? ["Chung", "Thêm", "Hình", "Màu"] 
    : ["Thông tin chung", "Thông tin thêm", "Hình ảnh", "Màu & Size"];
  
  const fullTabNames = {
    "Chung": "Thông tin chung",
    "Thêm": "Thông tin thêm", 
    "Hình": "Hình ảnh",
    "Màu": "Màu & Size"
  };

  const groupedFields = {
    "Thông tin chung": [
      {
        label: "Mã SKU",
        component: <CustomInput label="Mã SKU" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} fullWidth />,
      },
      {
        label: "Tên hàng",
        component: <CustomInput label="Tên hàng" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth />,
      },
      {
        label: "Phân loại",
        component: (
          <SelectField
            label="Phân loại"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            options={categories.map((category) => ({
              value: category.categoryId.toString(),
              label: category.name,
            }))}
            fullWidth
          />
        ),
      },
      {
        label: "Giá nhập",
        component: <CustomInput label="Giá nhập" type="number" value={formData.baseprice} onChange={(e) => setFormData({ ...formData, baseprice: e.target.value })} fullWidth />,
      },
      {
        label: "Sản phẩm mới",
        component: (
          <FormControlLabel
            control={<Switch checked={formData.isNew} onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })} />}
            label={isMobile ? "Mới" : "Sản phẩm mới"}
          />
        ),
      },
    ],
    "Thông tin thêm": [
      {
        label: "Mô tả ngắn",
        component: <CustomTextArea initialContent={formData.shortDescription} onChange={(val) => setFormData({ ...formData, shortDescription: val })} fullWidth />,
      },
      {
        label: "Mô tả chi tiết",
        component: <CustomTextArea initialContent={formData.description} onChange={(val) => setFormData({ ...formData, description: val })} fullWidth />,
      },
    ],
    "Hình ảnh": [
      {
        label: "Tags",
        component: <CustomTagSelector value={formData.tags} onChange={(tags) => setFormData({ ...formData, tags })} fullWidth={isMobile} />,
      },
      {
        label: "Hình ảnh sản phẩm",
        component: <CustomImageUploader onUpload={handleImageUpload} value={formData.imageUrls} onChange={handleImageUpload} compact={isMobile} />,
      },
    ],
    "Màu & Size": [
      {
        label: "Quản lý Màu & Size",
        component: <ColorSizeManager variations={formData.variations} setVariations={(variations) => setFormData({ ...formData, variations })} compact={isMobile} />,
      },
    ],
  };

  // Lấy tên tab đầy đủ để hiển thị
  const getFullTabName = () => {
    const currentTab = tabs[activeTab];
    return fullTabNames[currentTab] || currentTab;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        transform: "translate(-50%, -50%)", 
        width: isMobile ? "95%" : "90%", 
        maxWidth: { xs: "95%", sm: "90%", md: "900px", lg: "1000px" }, 
        bgcolor: "background.paper", 
        boxShadow: 10, 
        p: isMobile ? 2 : 3, 
        borderRadius: 2, 
        display: "flex", 
        flexDirection: "column", 
        maxHeight: isMobile ? "90vh" : "85vh", 
        overflow: "hidden" 
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
          Thêm Sản Phẩm
        </Typography>
        
        <CustomTabs 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          tabLabels={tabs} 
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? true : false}
        />
        
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
          {getFullTabName()}
        </Typography>
        
        <Box sx={{ 
          flexGrow: 1, 
          overflowY: "auto", 
          p: isMobile ? 1 : 2, 
          border: "1px solid #eee", 
          borderRadius: 1,
          maxHeight: isMobile ? "50vh" : "55vh"
        }}>
          {activeTab === tabs.indexOf(isMobile ? "Hình" : "Hình ảnh") ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
              {groupedFields["Hình ảnh"].map((field, index) => (
                <Box key={index} sx={{ width: "100%", maxWidth: isMobile ? "100%" : "500px", mb: 2 }}>
                  {field.component}
                </Box>
              ))}
            </Box>
          ) : activeTab === tabs.indexOf(isMobile ? "Màu" : "Màu & Size") ? (
            <Box sx={{ width: "100%" }}>
              {groupedFields["Màu & Size"].map((field, index) => (
                <Box key={index} sx={{ width: "100%" }}>
                  {field.component}
                </Box>
              ))}
            </Box>
          ) : (
            <Grid container spacing={isMobile ? 1 : 2}>
              {groupedFields[getFullTabName()]?.map((field, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={getFullTabName() === "Thông tin chung" ? 6 : 12} 
                  key={index} 
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  {field.component}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          mt: 2, 
          p: 1, 
          borderTop: "1px solid #ddd", 
          gap: isMobile ? 1 : 2,
          flexDirection: isMobile ? "column" : "row"
        }}>
          <Button 
            variant="contained" 
            color="success" 
            sx={{ 
              width: isMobile ? "100%" : "40%", 
              fontWeight: "bold",
              fontSize: isMobile ? "0.875rem" : "1rem"
            }} 
            onClick={handleSave} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            sx={{ 
              width: isMobile ? "100%" : "40%", 
              fontWeight: "bold",
              fontSize: isMobile ? "0.875rem" : "1rem"
            }} 
            onClick={onClose}
          >
            Bỏ qua
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddProductModal;